// src/pages/CountdownTimers.js
import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/axiosInstance';
import { Link, useOutletContext } from 'react-router-dom';
import { FiPlus, FiBarChart2, FiCopy, FiX, FiTrash2, FiEdit, FiEye, FiAlertCircle, FiCheckCircle, FiChevronRight, FiSearch, FiLink } from 'react-icons/fi';

const ITEMS_PER_PAGE = 10; // Adjusted for a table layout

// --- Main Countdown Timers Component ---
const CountdownTimers = () => {
    const [setBreadcrumb] = useOutletContext();
    const [timers, setTimers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // State for Search and Pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Modal States
    const [previewModal, setPreviewModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);

    useEffect(() => {
        setBreadcrumb(['Countdown']);
        fetchData();
    }, [setBreadcrumb]);

    const fetchData = async () => {
        setIsLoading(true);
        setError('');
        try {
            // Step 1: Fetch the initial list of all timers
            const timersRes = await api.get('/countdown/list');
            const initialTimers = timersRes.data.timer;

            // Step 2: As requested, fetch the view count for each timer individually
            const countPromises = initialTimers.map(timer =>
                api.get(`/logs/count/timer/${timer._id}`).then(res => ({
                    ...timer,
                    count: res.data.totalRequests,
                })).catch(() => ({
                    ...timer,
                    count: 'N/A', // Gracefully handle if a single count fails
                }))
            );

            // Step 3: Wait for all count requests to complete
            const timersWithCounts = await Promise.all(countPromises);
            setTimers(timersWithCounts);

        } catch (err) {
            setError(err.message === 'Network Error' ? 'No Internet Connection' : 'Failed to fetch data.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteTimer = async (id) => {
        try {
            await api.delete(`/countdown/${id}`);
            fetchData();
            setDeleteModal(null);
        } catch (err) {
            setError(err.message === 'Network Error' ? 'No Internet Connection.' : 'Failed to delete timer.');
        }
    };

    // Memoized filtering by name OR id
    const filteredTimers = useMemo(() => {
        if (!searchTerm) return timers;
        const lowercasedFilter = searchTerm.toLowerCase();
        return timers.filter(timer =>
            timer.name.toLowerCase().includes(lowercasedFilter) ||
            timer._id.toLowerCase().includes(lowercasedFilter)
        );
    }, [timers, searchTerm]);

    const paginatedTimers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredTimers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredTimers, currentPage]);

    const totalPages = Math.ceil(filteredTimers.length / ITEMS_PER_PAGE);

    if (isLoading) {
        return (
            <>
                <div className="flex items-center justify-center w-full h-[90vh]">
                    <div role="status">
                        <svg aria-hidden="true" className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" /><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" /></svg>
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <div className="p-8 text-center text-red-500 bg-red-50 rounded-lg">
                <FiAlertCircle className="mx-auto text-4xl mb-2" />
                <h3 className="text-lg font-semibold">An Error Occurred</h3>
                <p>{error}</p>
                <button onClick={fetchData} className="mt-4 py-2 px-4 text-sm font-medium text-white bg-brand-accent rounded-lg hover:bg-blue-700">
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <>
            {previewModal && <PreviewStatsModal timer={previewModal} onClose={() => setPreviewModal(null)} />}
            {deleteModal && <DeleteConfirmationModal timer={deleteModal} onConfirm={deleteTimer} onCancel={() => setDeleteModal(null)} />}

            <div className="p-4 sm:p-6 bg-brand-background min-h-screen font-sans">
                {/* --- Header --- */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-brand-heading">Countdown Timers</h1>
                        <p className="text-brand-paragraph mt-1">Manage, preview, and analyze your timers.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to="/countdown/analytics" className="flex items-center gap-2 py-2 px-4 text-sm font-medium text-brand-accent bg-white border border-brand-border rounded-lg hover:bg-gray-50 transition-colors">
                            <FiBarChart2 />
                            Analytics
                        </Link>
                        <Link to="createtimer" className="flex items-center gap-2 py-2 px-4 text-sm font-medium text-white bg-brand-accent rounded-lg hover:bg-blue-700 transition-colors">
                            <FiPlus />
                            Create Timer
                        </Link>
                    </div>
                </div>

                {/* --- Horizontal Plan Card --- */}
                <div className="bg-white p-5 rounded-lg border border-brand-border shadow-sm mb-8 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-brand-accent-light text-brand-accent rounded-lg">
                            <img src={`/images/CountdownTimerIcon.svg`} alt="Countdown Timer" width={24} height={24} />
                        </div>
                        <div>
                            <p className="font-bold text-brand-heading">Email Countdown Enterprise</p>
                            <p className="text-sm text-brand-paragraph">A
                                <svg
                                    viewBox="11.32 132.11 187.37 32.78"
                                    xmlns="http://www.w3.org/2000/svg"
                                    height={12} className='inline mx-1'
                                >
                                    <g transform="matrix(0.41864164, 0, 0, 0.41864164, -109.34452, -12.25839)">
                                        <g id="logo-center">
                                            <g id="title">
                                                <path
                                                    id="path139560"
                                                    d="m 362.79687,-43.344 22.104,43.416 5.904,-11.808 -16.056,-31.608 z m 21.60001,0.072 21.95999,43.344 5.976,-11.808 -15.98399,-31.536 z m 32.976,0.072 c -3.67201,0 -6.69601,3.024 -6.69601,6.696 0,3.672 3.024,6.696 6.69601,6.696 3.74399,0 6.76799,-3.024 6.76799,-6.696 0,-3.672 -3.024,-6.696 -6.76799,-6.696 z"
                                                    strokeWidth="0"
                                                    strokeLinejoin="miter"
                                                    strokeMiterlimit="2"
                                                    fill="#1a005a"
                                                    stroke="#1a005a"
                                                    transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"
                                                />
                                                <path
                                                    id="path139562"
                                                    d="m 436.778,-20.088 c -0.792,-5.472 1.008,-10.656 4.536,-14.4 -9.36,1.512 -15.768,10.296 -14.4,19.656 1.368,9.504 10.224,16.056 19.656,14.688 4.104,-0.576 7.632,-2.52 10.224,-5.328 -0.144,0 -0.216,0.072 -0.36,0.072 -9.504,1.368 -18.288,-5.184 -19.656,-14.688 z m 11.736,-2.736 -8.064,6.336 h 16.056 c 4.464,-11.592 -4.608,-18 -12.24,-18 5.616,5.4 4.248,11.664 4.248,11.664 z"
                                                    strokeWidth="0"
                                                    strokeLinejoin="miter"
                                                    strokeMiterlimit="2"
                                                    fill="#1f1169"
                                                    stroke="#1f1169"
                                                    transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"
                                                />
                                                <path
                                                    id="path139564"
                                                    d="m 497.402,-22.968 c -3.168,-8.856 -12.816,-13.536 -21.672,-10.584 4.824,1.656 8.784,5.4 10.656,10.512 3.168,8.928 -1.512,18.792 -10.512,21.96 -0.072,0 -0.216,0.072 -0.288,0.072 3.528,1.224 7.56,1.296 11.376,0 8.928,-3.168 13.608,-13.032 10.44,-21.96 z M 463.85,-0.432 c 0.864,0.288 1.728,0.432 2.736,0.432 4.32,0 7.848,-3.528 7.848,-7.848 V -43.272 H 463.85 Z"
                                                    strokeWidth="0"
                                                    strokeLinejoin="miter"
                                                    strokeMiterlimit="2"
                                                    fill="#232278"
                                                    stroke="#232278"
                                                    transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"
                                                />
                                                <path
                                                    id="path139566"
                                                    d="m 515.21525,0 v -43.272 h -10.584 V 0 Z m 12.168,-34.488 c -1.728,0 -3.456,0.576 -4.824,1.728 l -5.4,4.176 h 7.488 V 0 h 10.656 v -26.568 c 0,-4.392 -3.528,-7.92 -7.92,-7.92 z"
                                                    strokeWidth="0"
                                                    strokeLinejoin="miter"
                                                    strokeMiterlimit="2"
                                                    fill="#283387"
                                                    stroke="#283387"
                                                    transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"
                                                />
                                                <path
                                                    id="path139568"
                                                    d="M 553.184,-28.584 H 542.6 V -7.92 c 0,4.392 3.528,7.92 7.848,7.92 1.8,0 3.528,-0.576 4.896,-1.728 l 5.184,-4.032 h -7.344 z m -5.256,-3.96 c 2.952,0 5.328,-2.376 5.328,-5.328 0,-2.952 -2.376,-5.4 -5.328,-5.4 -2.952,0 -5.328,2.448 -5.328,5.4 0,2.952 2.376,5.328 5.328,5.328 z"
                                                    strokeWidth="0"
                                                    strokeLinejoin="miter"
                                                    strokeMiterlimit="2"
                                                    fill="#2c4497"
                                                    stroke="#2c4497"
                                                    transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"
                                                />
                                                <path
                                                    id="path139570"
                                                    d="m 580.60025,-34.488 -17.424,34.56 h 11.88 l 17.496,-34.56 z m 11.952,34.56 v -10.296 l -12.312,10.296 z m -29.376,-34.56 v 10.224 l 12.24,-10.224 z"
                                                    strokeWidth="0"
                                                    strokeLinejoin="miter"
                                                    strokeMiterlimit="2"
                                                    fill="#3155a6"
                                                    stroke="#3155a6"
                                                    transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"
                                                />
                                                <path
                                                    id="path139572"
                                                    d="m 615.05337,-34.488 -17.424,34.56 h 11.88 l 17.496,-34.56 z m 11.952,34.56 V -10.224 L 614.69338,0.072 Z m -29.376,-34.56 v 10.224 l 12.24,-10.224 z"
                                                    strokeWidth="0"
                                                    strokeLinejoin="miter"
                                                    strokeMiterlimit="2"
                                                    fill="#3566b5"
                                                    stroke="#3566b5"
                                                    transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"
                                                />
                                                <path
                                                    id="path139574"
                                                    d="m 639.8585,0 c -2.448,0 -4.392,2.016 -4.392,4.464 0,2.376 1.944,4.392 4.392,4.392 2.448,0 4.392,-2.016 4.392,-4.392 0,-2.448 -1.944,-4.464 -4.392,-4.464 z m 15.912,-34.488 c -3.024,0 -5.4,2.376 -5.4,5.4 0,2.952 2.376,5.4 5.4,5.4 2.952,0 5.4,-2.448 5.4,-5.4 0,-3.024 -2.448,-5.4 -5.4,-5.4 z m -26.208,0 17.424,34.56 5.976,-11.808 -11.448,-22.752 z"
                                                    strokeWidth="0"
                                                    strokeLinejoin="miter"
                                                    strokeMiterlimit="2"
                                                    fill="#3a77c4"
                                                    stroke="#3a77c4"
                                                    transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"
                                                />
                                            </g>
                                        </g>
                                    </g>
                                </svg>
                                Product</p>
                        </div>
                    </div>
                    <Link to="/countdown/billing" className="group flex items-center text-sm font-medium text-brand-accent hover:text-blue-700">
                        <span>Manage Billing</span>
                        <FiChevronRight className="transition-transform group-hover:translate-x-1" />
                    </Link>
                </div>


                {/* --- Table Container --- */}
                <div className="bg-white rounded-lg border border-brand-border shadow-sm">
                    {/* --- Search Bar --- */}
                    <div className="p-4 border-b border-gray-100">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <FiSearch className="w-5 h-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className="bg-gray-50 border border-brand-border text-brand-paragraph text-sm rounded-lg focus:ring-brand-accent focus:border-brand-accent block w-full pl-10 p-3"
                                placeholder="Search by name or ID..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1); // Reset to first page on new search
                                }}
                            />
                        </div>
                    </div>

                    {/* --- Timers Table --- */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-brand-paragraph">
                            <thead className="text-xs text-brand-heading uppercase bg-gray-50">
                                <tr>
                                    <th scope="col" className="py-3 px-6">Timer Name</th>
                                    <th scope="col" className="py-3 px-6">ID</th>
                                    <th scope="col" className="py-3 px-6">Views</th>
                                    <th scope="col" className="py-3 px-6">Status</th>
                                    <th scope="col" className="py-3 px-6 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedTimers.length > 0 ? paginatedTimers.map(timer => (
                                    <TimerRow key={timer._id} timer={timer} onPreview={setPreviewModal} onDelete={setDeleteModal} />
                                )) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-16">
                                            <FiSearch className="mx-auto text-4xl mb-2 text-gray-300" />
                                            <h3 className="text-lg font-semibold">No Timers Found</h3>
                                            <p>{searchTerm ? `No results for "${searchTerm}"` : "Click 'Create Timer' to get started."}</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

// --- Child Component: TimerRow ---
const TimerRow = ({ timer, onPreview, onDelete }) => (
    <tr className="bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors">
        <th scope="row" className="py-4 px-6 font-normal text-brand-heading whitespace-nowrap">{timer.name}</th>
        <td className="py-4 px-6 font-mono text-gray-500">{timer._id}</td>
        <td className="py-4 px-6 font-medium text-brand-accent">
            {typeof timer.count === 'number' ? timer.count.toLocaleString() : timer.count}
        </td>
        <td className="py-4 px-6">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium text-brand-success bg-brand-success-light rounded-full">
                <FiCheckCircle /> Live
            </span>
        </td>
        <td className="py-4 px-6 text-center">
            <div className="flex items-center justify-center gap-2">
                <button onClick={() => onPreview(timer)} title="Preview" className="p-2 text-brand-accent rounded-md hover:bg-brand-accent-light transition-colors">
                    <FiEye size={18} />
                </button>
                <button onClick={() => onPreview(timer)} title="Embeddable URL" className="p-2 text-brand-accent rounded-md hover:bg-brand-accent-light transition-colors">
                    <FiLink size={18} />
                </button>
                <Link to={`edittimer/${timer._id}`} title="Edit" className="p-2 text-blue-600 rounded-md hover:bg-blue-50 transition-colors">
                    <FiEdit size={18} />
                </Link>
                <button onClick={() => onDelete(timer)} title="Delete" className="p-2 text-red-600 rounded-md hover:bg-red-50 transition-colors">
                    <FiTrash2 size={18} />
                </button>
            </div>
        </td>
    </tr>
);


// --- Child Component: Pagination ---
const Pagination = ({ currentPage, totalPages, onPageChange }) => (
    <div className="p-4 flex justify-between items-center text-sm text-brand-paragraph border-t border-gray-100">
        <span>
            Page <span className="font-medium text-brand-heading">{currentPage}</span> of <span className="font-medium text-brand-heading">{totalPages}</span>
        </span>
        <div className="inline-flex items-center space-x-2">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="py-2 px-3 leading-tight bg-white border border-brand-border rounded-lg hover:bg-gray-100 hover:text-brand-heading disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                Previous
            </button>
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="py-2 px-3 leading-tight bg-white border border-brand-border rounded-lg hover:bg-gray-100 hover:text-brand-heading disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                Next
            </button>
        </div>
    </div>
);

// --- Child Component: PreviewStatsModal ---
const PreviewStatsModal = ({ timer, onClose }) => {
    const [requestCount] = useState(timer.count); // Use count passed from parent
    const [isCopying, setIsCopying] = useState(false);
    const url = `https://countdown.webhizzy.in/${timer._id}`;

    const handleCopy = () => {
        navigator.clipboard.writeText(url);
        setIsCopying(true);
        setTimeout(() => setIsCopying(false), 2000);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4 transition-opacity">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md transform transition-all scale-100">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-brand-heading">Preview & Stats</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><FiX size={24} /></button>
                </div>
                <div className="space-y-5">
                    <div className="bg-gray-100 p-4 rounded-lg border border-gray-200">
                        <img src={`${url}?v=${new Date().getTime()}`} alt="Countdown Preview" className="mx-auto h-20" />
                    </div>
                    <div className="bg-brand-accent-light p-4 rounded-lg text-center border border-blue-200">
                        <p className="text-sm text-brand-accent font-medium uppercase tracking-wider">Total Views</p>
                        <p className="text-3xl font-bold text-brand-accent">{typeof requestCount === 'number' ? requestCount.toLocaleString() : requestCount}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-brand-paragraph mb-2">Embeddable URL</p>
                        <div className="flex gap-2">
                            <input type="text" value={url} readOnly className="w-full bg-gray-50 border border-brand-border rounded-md p-2 text-sm text-gray-600 focus:outline-none" />
                            <button onClick={handleCopy} className={`py-2 px-4 text-white rounded-md flex items-center justify-center gap-2 w-28 transition-colors ${isCopying ? 'bg-brand-success' : 'bg-brand-accent hover:bg-blue-700'}`}>
                                {isCopying ? <><FiCheckCircle /> Copied</> : <><FiCopy /> Copy</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Child Component: DeleteConfirmationModal ---
const DeleteConfirmationModal = ({ timer, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4 transition-opacity">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm text-center transform transition-all scale-100">
            <FiAlertCircle className="mx-auto text-5xl text-red-500 mb-4" />
            <h3 className="text-lg font-bold text-brand-heading mb-2">Delete Timer?</h3>
            <p className="text-brand-paragraph mb-1">
                Are you sure you want to delete <span className="font-bold">{timer.name}</span>?
            </p>
            <p className="text-sm text-gray-500 mb-6">This action cannot be undone.</p>
            <div className="flex justify-center gap-4">
                <button onClick={onCancel} className="py-2 px-6 text-sm text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200">
                    Cancel
                </button>
                <button onClick={() => onConfirm(timer._id)} className="py-2 px-6 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700">
                    Delete
                </button>
            </div>
        </div>
    </div>
);

export default CountdownTimers;
